import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { PurchaseOrderLineSuggestion } from "@/modules/inventory/domain/types";
import { formatLakDisplay, formatQtyDisplay } from "@/shared/lib/format-number";
import { cn } from "@/shared/lib/utils";
import { LightbulbIcon, Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { usePurchaseOrderSuggestedLinesQuery } from "../../api/purchase-orders-queries";
import { PO_SUGGESTION_REASON_LABELS } from "./purchaseLaoLabels";

type PurchaseOrderSuggestedLinesCardProps = {
  supplierId: string;
  existingItemKeys: Set<string>;
  onAdd: (items: PurchaseOrderLineSuggestion[]) => void;
};

function itemKey(s: PurchaseOrderLineSuggestion): string {
  return `${s.itemType}:${s.itemId}`;
}

export function PurchaseOrderSuggestedLinesCard({
  supplierId,
  existingItemKeys,
  onAdd,
}: PurchaseOrderSuggestedLinesCardProps) {
  const { data, isLoading, isError, refetch, isFetching } =
    usePurchaseOrderSuggestedLinesQuery(supplierId);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelected(new Set());
  }, [supplierId, data]);

  const rows = useMemo(() => data ?? [], [data]);

  const selectableKeys = useMemo(
    () =>
      rows
        .filter((r) => !existingItemKeys.has(itemKey(r)))
        .map((r) => itemKey(r)),
    [rows, existingItemKeys],
  );

  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((k) => selected.has(k));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableKeys));
  };

  const toggleOne = (key: string, disabled: boolean) => {
    if (disabled) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const picks = rows.filter((r) => selected.has(itemKey(r)));

  if (!supplierId) {
    return (
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <LightbulbIcon className="size-5 text-amber-500" />
            ລາຍການແນະນຳໃຫ້ສັ່ງ
          </CardTitle>
          <CardDescription>
            ເລືອກຜູ້ສະໜອງກ່ອນ — ຄິດຈາກເງື່ອນໄຂສຕັອກ (min/target) ທີ່ Hub, ຍອດຄົງ ແລະ PO ຄ້າງຮັບ
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <LightbulbIcon className="size-5 text-amber-500" />
              ລາຍການແນະນຳໃຫ້ສັ່ງ
            </CardTitle>
            <CardDescription>
              ເລືອກແລ້ວກົດເພີ່ມເຂົ້າບັນທັດ PO · ໃຊ້ເກັນໄຂຈາກ «ເງື່ອນໄຂສຕັອກ» (ມີ policy ເທົ່ານັ້ນ)
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isFetching}
            onClick={() => refetch()}
          >
            {isFetching ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : null}
            ໂຫຼດໃໝ່
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2Icon className="size-4 animate-spin" />
            ກຳລັງໂຫຼດຄຳແນະນຳ...
          </p>
        ) : isError ? (
          <p className="text-destructive text-sm">ໂຫຼດຄຳແນະນຳບໍ່ສຳເລັດ</p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            ບໍ່ມີລາຍການແນະນຳ — ຕັ້ງເງື່ອນໄຂສຕັອກ global ທີ່ Hub ຫຼື ຍອດຄົງພໍດີແລ້ວ
          </p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="ເລືອກທັງໝົດ"
                      disabled={selectableKeys.length === 0}
                    />
                  </TableHead>
                  <TableHead>ລາຍການ</TableHead>
                  <TableHead className="text-right">ຄົງ Hub</TableHead>
                  <TableHead className="text-right">ຂັ້ນຕ່ຳ</TableHead>
                  <TableHead className="text-right">ເປົ້າ</TableHead>
                  <TableHead className="text-right">PO ຄ້າງ</TableHead>
                  <TableHead className="text-right">ແນະນຳສັ່ງ</TableHead>
                  <TableHead className="text-right">ລາຄາ/ຫນ່ວຍ</TableHead>
                  <TableHead>ເຫດຜົນ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const key = itemKey(row);
                  const alreadyInPo = existingItemKeys.has(key);
                  const checked = selected.has(key);
                  return (
                    <TableRow
                      key={key}
                      className={cn(
                        checked && "bg-primary/5",
                        alreadyInPo && "opacity-60",
                      )}
                    >
                      <TableCell>
                        <Checkbox
                          checked={checked}
                          disabled={alreadyInPo}
                          onCheckedChange={() => toggleOne(key, alreadyInPo)}
                          aria-label={`ເລືອກ ${row.itemName}`}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="text-left font-medium hover:underline disabled:no-underline"
                          disabled={alreadyInPo}
                          onClick={() => toggleOne(key, alreadyInPo)}
                        >
                          {row.itemName}
                          <span className="ml-1 text-muted-foreground text-xs">
                            ({row.unit})
                          </span>
                        </button>
                        {alreadyInPo ? (
                          <p className="text-muted-foreground text-xs">
                            ມີໃນບັນທັດ PO ແລ້ວ
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQtyDisplay(row.hubQtyAvailable)} {row.unit}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQtyDisplay(row.minQty)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQtyDisplay(row.targetQty)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatQtyDisplay(row.qtyOnOpenPo)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {formatQtyDisplay(row.suggestedQty)} {row.unit}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatLakDisplay(row.unitCostLak)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {PO_SUGGESTION_REASON_LABELS[row.reason]}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="mt-4 flex justify-end">
              <Button
                type="button"
                disabled={picks.length === 0}
                onClick={() => {
                  onAdd(picks);
                  setSelected(new Set());
                }}
              >
                ເພີ່ມເຂົ້າບັນທັດ ({picks.length})
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
